import { DonutChart, DonutLegend } from "@/components/dashboard/DonutChart";
import { marketplaceLabel } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import {
  WEEKDAY_LABELS,
  buildHeatGrid,
  heatLevel,
  percent,
  shortDayLabel,
  type ChannelSeries,
  type HeatCell,
  type LabelCount,
  type PlatformRow,
} from "@/lib/analytics";
import { EmptyChart, chartColor, fmt } from "./shared";

// ---------------------------------------------------------------------------
// Heatmap dia da semana x hora
// ---------------------------------------------------------------------------

const HEAT_CLASSES = [
  "bg-[hsl(var(--border-strong)/0.35)]",
  "bg-[hsl(150_54%_51%/0.25)]",
  "bg-[hsl(150_54%_51%/0.45)]",
  "bg-[hsl(150_54%_51%/0.7)]",
  "bg-[hsl(150_54%_51%)]",
] as const;

const HOURS = Array.from({ length: 24 }, (_, h) => h);

export function ClickHeatmap({ cells }: { readonly cells: readonly HeatCell[] }): JSX.Element {
  const { grid, max, best } = buildHeatGrid(cells);
  if (!best) {
    return <EmptyChart message="Ainda não há cliques suficientes para indicar o melhor horário." />;
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-foreground">
        Melhor horário para divulgar:{" "}
        <strong className="text-[hsl(var(--primary))]">
          {WEEKDAY_LABELS[best.dow]} às {String(best.hour).padStart(2, "0")}h
        </strong>{" "}
        <span className="text-muted-foreground">({fmt(best.count)} cliques, horário de Brasília)</span>
      </p>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[560px] table-fixed border-separate border-spacing-[3px]" aria-label="Cliques por dia da semana e hora">
          <thead>
            <tr>
              <th scope="col" className="w-8"><span className="sr-only">Dia</span></th>
              {HOURS.map((h) => (
                <th key={h} scope="col" className="overflow-visible whitespace-nowrap text-left text-[9px] font-normal text-muted-foreground">
                  {h % 3 === 0 ? `${h}h` : <span className="sr-only">{h}h</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, dow) => (
              <tr key={dow}>
                <th scope="row" className="pr-1 text-left text-[10px] font-normal text-muted-foreground">{WEEKDAY_LABELS[dow]}</th>
                {row.map((count, hour) => (
                  <td
                    key={hour}
                    title={`${WEEKDAY_LABELS[dow]} ${hour}h: ${fmt(count)} cliques`}
                    aria-label={`${WEEKDAY_LABELS[dow]} ${hour}h: ${count} cliques`}
                    className={cn("h-6 rounded-[3px]", HEAT_CLASSES[heatLevel(count, max)])}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground" aria-hidden="true">
        menos
        {HEAT_CLASSES.map((c) => (
          <span key={c} className={cn("h-3 w-3 rounded-[3px]", c)} />
        ))}
        mais
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Serie temporal por canal (linhas)
// ---------------------------------------------------------------------------

const SERIES_W = 600;
const SERIES_H = 140;

function linePath(values: readonly number[], max: number): string {
  const step = values.length > 1 ? SERIES_W / (values.length - 1) : 0;
  return values
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(SERIES_H - (v / max) * (SERIES_H - 6) - 3).toFixed(1)}`)
    .join(" ");
}

export function ChannelSeriesChart({ series, days }: { readonly series: readonly ChannelSeries[]; readonly days: readonly string[] }): JSX.Element {
  const visible = series.filter((s) => s.values.some((v) => v > 0));
  if (visible.length === 0 || days.length === 0) {
    return <EmptyChart message="Nenhum clique por canal no período." />;
  }
  const max = Math.max(1, ...visible.flatMap((s) => s.values));
  return (
    <div className="flex flex-col gap-3">
      <svg viewBox={`0 0 ${SERIES_W} ${SERIES_H}`} preserveAspectRatio="none" className="h-36 w-full" role="img" aria-label="Cliques por canal ao longo do período">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={SERIES_W} y1={SERIES_H * f} y2={SERIES_H * f} stroke="hsl(var(--border))" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        {visible.map((s, i) => (
          <path key={s.channel} d={linePath(s.values, max)} fill="none" stroke={chartColor(i)} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{shortDayLabel(days[0]!)}</span>
        <span>{shortDayLabel(days[days.length - 1]!)}</span>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {visible.map((s, i) => (
          <li key={s.channel} className="flex items-center gap-1.5 text-xs text-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: chartColor(i) }} aria-hidden="true" />
            {s.channel}
            <span className="font-mono tabular-nums text-muted-foreground">{fmt(s.values.reduce((a, b) => a + b, 0))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparativo de plataformas: cliques x links
// ---------------------------------------------------------------------------

export function PlatformComparison({ rows }: { readonly rows: readonly PlatformRow[] }): JSX.Element {
  if (rows.length === 0) {
    return <EmptyChart message="Nenhum link ou clique por plataforma no período." />;
  }
  const max = Math.max(1, ...rows.flatMap((r) => [r.clicks, r.links]));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-4 text-[11px] text-muted-foreground" aria-hidden="true">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(42_80%_57%)]" />Cliques</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(200_70%_50%)]" />Links criados</span>
      </div>
      <ul className="flex flex-col gap-3">
        {rows.map((r) => (
          <li key={r.marketplace} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">{marketplaceLabel(r.marketplace)}</span>
              <span className="font-mono tabular-nums text-muted-foreground">
                {fmt(r.clicks)} cliques · {fmt(r.links)} links
                {r.links > 0 && r.clicks > 0 && ` · ${(r.clicks / r.links).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} por link`}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[hsl(var(--border-strong))]">
              <div className="h-full rounded-full bg-[hsl(42_80%_57%)]" style={{ width: `${percent(r.clicks, max)}%` }} />
            </div>
            <div className="h-1.5 rounded-full bg-[hsl(var(--border-strong))]">
              <div className="h-full rounded-full bg-[hsl(200_70%_50%)]" style={{ width: `${percent(r.links, max)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Distribuicao (dispositivo, navegador, SO)
// ---------------------------------------------------------------------------

export function BreakdownDonut({ items, labelMap, emptyMessage }: { readonly items: readonly LabelCount[]; readonly labelMap?: Record<string, string>; readonly emptyMessage: string }): JSX.Element {
  if (items.length === 0) {
    return <EmptyChart message={emptyMessage} />;
  }
  const slices = items.slice(0, 6).map((it, i) => ({ label: labelMap?.[it.label] ?? it.label, value: it.count, color: chartColor(i) }));
  return (
    <div className="flex flex-col items-center gap-4">
      <DonutChart slices={slices} size={100} strokeWidth={20} />
      <DonutLegend slices={slices} className="w-full" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista ranqueada (cidades, paises, referrers)
// ---------------------------------------------------------------------------

export interface RankedItem {
  readonly key: string;
  readonly label: string;
  readonly detail?: string | null;
  readonly count: number;
}

export function RankedList({ items, total, emptyMessage }: { readonly items: readonly RankedItem[]; readonly total: number; readonly emptyMessage: string }): JSX.Element {
  if (items.length === 0) {
    return <EmptyChart message={emptyMessage} />;
  }
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <ol className="flex flex-col gap-2.5">
      {items.map((it, i) => (
        <li key={it.key} className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-foreground">
              <span className="mr-1.5 font-mono text-muted-foreground">{i + 1}.</span>
              {it.label}
              {it.detail && <span className="ml-1 text-muted-foreground">{it.detail}</span>}
            </span>
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
              {fmt(it.count)} ({percent(it.count, total)}%)
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[hsl(var(--border-strong))]">
            <div className="h-full rounded-full" style={{ width: `${percent(it.count, max)}%`, backgroundColor: chartColor(i) }} />
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Linha de tendencia (escala min-max, altura fixa pelo className)
// ---------------------------------------------------------------------------

export function TrendLine({ values, color, label, className }: { readonly values: readonly number[]; readonly color: string; readonly label: string; readonly className?: string }): JSX.Element | null {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const range = Math.max(...values) - min || 1;
  const step = 100 / (values.length - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(2)},${(38 - ((v - min) / range) * 36).toFixed(2)}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className={cn("w-full", className)} role="img" aria-label={label}>
      <polygon points={`0,40 ${points} 100,40`} fill={color} fillOpacity="0.12" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}
